"use client";

import { useState } from "react";
import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import PageHero from "@/components/sections/PageHero";
import {
  HiMail,
  HiPhone,
  HiLocationMarker,
  HiClock,
  HiGlobeAlt,
  HiShieldCheck,
  HiCheckCircle,
  HiArrowRight,
  HiChevronDown,
} from "react-icons/hi";
import { IoPaperPlane, IoChatboxEllipses } from "react-icons/io5";
import { CgSpinner } from "react-icons/cg";
import { Button } from "@/components/ui/button";
import { useLanguage, useTranslations } from "@/lib/i18n";

export default function ContactPage() {
  const { language } = useLanguage();
  const t = useTranslations();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    topic: t.contact.subjects[0],
    message: "",
  });

  const [isLoading, setIsLoading] = useState(false);

  const pageContent = language === "id" ? {
    title: "Hubungi KinKen",
    subtitle: "Gerbang Anda menuju hunian premium di Bali. Tim concierge kami tersedia 24/7 untuk membantu pertanyaan, pemesanan, dan konsultasi personal.",
    superText: "PREMIUM CONCIERGE SERVICE",
    contactInfo: "Informasi Kontak",
    contactDesc: "Apakah Anda memiliki pertanyaan tentang ketersediaan kamar, ingin menjadwalkan private viewing, atau butuh informasi tentang fasilitas premium, tim kami siap membantu.",
    premiumLocation: "Lokasi Premium",
    phoneWhatsApp: "Telepon & WhatsApp",
    emailAddress: "Alamat Email",
    conciergeHours: "Jam Concierge",
    whatsappSupport: "Dukungan WhatsApp 24/7",
    officeVisits: "Kunjungan Kantor: Sen-Jum 09:00-18:00",
    languages: "Bahasa yang Dilayani",
    trustSecurity: "Kepercayaan & Keamanan",
    security: "Keamanan",
    sendMessage: "KIRIM PESAN",
    formTitle: "Hubungi Concierge",
    formDesc: "Isi form di bawah dan tim concierge kami akan memberikan respons personal dalam",
    minutes: "15 menit",
    firstName: "Nama Depan",
    lastName: "Nama Belakang",
    emailLabel: "Alamat Email",
    phoneLabel: "Nomor Telepon",
    topicLabel: "Topik Pertanyaan",
    messageLabel: "Pesan Anda",
    messagePlaceholder: "Mohon berikan detail pertanyaan Anda, tanggal yang diinginkan, dan persyaratan khusus. Tim concierge kami akan merespons dengan opsi yang dipersonalisasi...",
    processing: "Memproses",
    sendConcierge: "Kirim Pesan ke Concierge",
    sendShort: "Kirim Pesan",
    disclaimer: "Informasi Anda aman. Dengan mengirimkan, Anda setuju untuk menerima komunikasi dari KinKen Kost Elite terkait pertanyaan Anda.",
    quickQuestions: "Pertanyaan Cepat?",
    quickDesc: "Kunjungi halaman FAQ kami untuk jawaban instan tentang pembayaran, fasilitas, kebijakan, dan",
    luxuryLiving: "luxury living",
    viewFaq: "Lihat Pertanyaan yang Sering Diajukan",
  } : {
    title: "Contact KinKen",
    subtitle: "Your gateway to premium living in Bali. Our dedicated concierge team is available 24/7 to assist with inquiries, bookings, and personalized consultations.",
    superText: "PREMIUM CONCIERGE SERVICE",
    contactInfo: "Contact Information",
    contactDesc: "Whether you have questions about room availability, want to schedule a private viewing, or need information about our premium amenities, our team is here to help.",
    premiumLocation: "Premium Location",
    phoneWhatsApp: "Phone & WhatsApp",
    emailAddress: "Email Address",
    conciergeHours: "Concierge Hours",
    whatsappSupport: "24/7 WhatsApp Support",
    officeVisits: "Office Visits: Mon-Fri 09:00-18:00",
    languages: "Languages Spoken",
    trustSecurity: "Trust & Security",
    security: "Security",
    sendMessage: "SEND A MESSAGE",
    formTitle: "Get in Touch with Concierge",
    formDesc: "Fill out the form below and our dedicated concierge team will provide a personalized response within",
    minutes: "15 minutes",
    firstName: "First Name",
    lastName: "Last Name",
    emailLabel: "Email Address",
    phoneLabel: "Phone Number",
    topicLabel: "Topic of Inquiry",
    messageLabel: "Your Message",
    messagePlaceholder: "Please provide details about your inquiry, preferred dates, and any specific requirements. Our concierge will respond with personalized options...",
    processing: "Processing",
    sendConcierge: "Send Message to Concierge",
    sendShort: "Send Message",
    disclaimer: "Your information is secure. By submitting, you agree to receive communication from KinKen Kost Elite regarding your inquiry.",
    quickQuestions: "Quick Questions?",
    quickDesc: "Check our FAQ page for instant answers about payments, amenities, policies, and",
    luxuryLiving: "luxury living",
    viewFaq: "View Frequently Asked Questions",
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    setTimeout(() => {
      const adminPhone = "6281916401702";
      const greeting = language === "id" 
        ? "*Halo Tim Concierge KinKen Kost Elite,* 👋\nSaya menghubungi melalui halaman Contact Us."
        : "*Hello KinKen Kost Elite Concierge Team,* 👋\nI'm reaching out via the Contact Us page.";
      
      const text = `
${greeting}

👤 *${language === "id" ? "Data Diri" : "Personal Information"}:*
${language === "id" ? "Nama" : "Name"}: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
${language === "id" ? "No. HP" : "Phone"}: ${formData.phone}

📌 *${language === "id" ? "Topik" : "Topic"}:*
${formData.topic}

💬 *${language === "id" ? "Pesan" : "Message"}:*
${formData.message}

${language === "id" ? "Mohon bantuannya, terima kasih." : "Looking forward to your response, thank you."}
      `.trim();

      const url = `https://wa.me/${adminPhone}?text=${encodeURIComponent(text)}`;
      window.open(url, "_blank");

      setIsLoading(false);
    }, 1000);
  };

  return (
    <main className="min-h-screen bg-white">
      <Navbar />

      <PageHero
        title={pageContent.title}
        subtitle={pageContent.subtitle}
        image="https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=2070&auto=format&fit=crop"
        superText={pageContent.superText}
      />

      <section className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 shadow-2xl rounded-sm overflow-hidden">
            {/* LEFT COLUMN: CONTACT INFO */}
            <div className="lg:col-span-5 bg-brand-charcoal text-white p-10 md:p-16 flex flex-col justify-between relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-gold rounded-full mix-blend-overlay filter blur-[80px] opacity-20 pointer-events-none"></div>

              <div>
                <h3 className="font-serif text-3xl mb-6">{pageContent.contactInfo}</h3>
                <p className="text-gray-400 font-light text-sm leading-relaxed mb-10 font-sans">
                  {pageContent.contactDesc}
                </p>

                <div className="space-y-8">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <HiLocationMarker className="text-brand-gold" size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-1 font-sans">
                        {pageContent.premiumLocation}
                      </h4>
                      <p className="text-gray-400 text-sm font-light leading-relaxed font-sans">
                        Gg. Mawar No.8, Sidakarya<br />
                        Denpasar Selatan, Bali 80224<br />
                        <span className="text-brand-gold text-xs mt-1 block">
                          10 mins to Sanur Beach • 25 mins to Airport
                        </span>
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <HiPhone className="text-brand-gold" size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-1 font-sans">
                        {pageContent.phoneWhatsApp}
                      </h4>
                      <p className="text-gray-400 text-sm font-light font-sans">
                        +62 819 1640 1702 (Concierge)
                      </p>
                      <p className="text-gray-500 text-xs mt-1 font-sans">
                        Fast Response • Bilingual Support
                      </p>
                      <div className="flex items-center gap-2 mt-2">
                        <HiCheckCircle size={12} className="text-green-400" />
                        <span className="text-xs text-gray-400 font-sans">
                          {language === "id" ? "Rata-rata respons: 15 menit" : "Average response: 15 minutes"}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <HiMail className="text-brand-gold" size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-1 font-sans">
                        {pageContent.emailAddress}
                      </h4>
                      <p className="text-gray-400 text-sm font-light font-sans">
                        concierge@kinken-kost.com
                      </p>
                      <p className="text-gray-400 text-sm font-light font-sans">
                        reservations@kinken-kost.com
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <HiClock className="text-brand-gold" size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-1 font-sans">
                        {pageContent.conciergeHours}
                      </h4>
                      <p className="text-gray-400 text-sm font-light font-sans">
                        {pageContent.whatsappSupport}
                      </p>
                      <p className="text-gray-500 text-xs mt-1 font-sans">
                        {pageContent.officeVisits}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-brand-gold/20 flex items-center justify-center shrink-0">
                      <HiGlobeAlt className="text-brand-gold" size={18} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold uppercase tracking-wider mb-1 font-sans">
                        {pageContent.languages}
                      </h4>
                      <div className="flex flex-wrap gap-2 mt-2">
                        <span className="px-3 py-1.5 bg-white/10 rounded text-xs font-sans">Bahasa Indonesia</span>
                        <span className="px-3 py-1.5 bg-white/10 rounded text-xs font-sans">English</span>
                        <span className="px-3 py-1.5 bg-white/10 rounded text-xs font-sans">中文 (Chinese)</span>
                        <span className="px-3 py-1.5 bg-white/10 rounded text-xs font-sans">日本語 (Japanese)</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-12 pt-8 border-t border-white/10">
                <div className="flex items-center gap-3 mb-4">
                  <HiShieldCheck size={16} className="text-brand-gold" />
                  <p className="text-xs text-gray-400 uppercase tracking-wider font-sans">
                    {pageContent.trustSecurity}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-white/5 rounded">
                    <div className="text-lg font-bold text-white font-sans">24/7</div>
                    <div className="text-xs text-gray-400 font-sans">{pageContent.security}</div>
                  </div>
                  <div className="text-center p-3 bg-white/5 rounded">
                    <div className="text-lg font-bold text-white font-sans">SSL</div>
                    <div className="text-xs text-gray-400 font-sans">Encrypted</div>
                  </div>
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: FORM */}
            <div className="lg:col-span-7 bg-white p-10 md:p-16">
              <span className="text-brand-gold text-xs font-bold tracking-[0.25em] uppercase block mb-4 font-sans">
                {pageContent.sendMessage}
              </span>
              <h2 className="font-serif text-3xl md:text-4xl text-brand-charcoal mb-4">
                {pageContent.formTitle}
              </h2>
              <p className="text-gray-500 font-light text-sm mb-8 font-sans">
                {pageContent.formDesc}{" "}
                <span className="font-medium text-brand-gold">{pageContent.minutes}</span>.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                      {pageContent.firstName}
                    </label>
                    <input
                      required
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      type="text"
                      className="w-full bg-[#FAFAFA] border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors font-sans"
                      placeholder="John"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                      {pageContent.lastName}
                    </label>
                    <input
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      type="text"
                      className="w-full bg-[#FAFAFA] border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors font-sans"
                      placeholder="Doe"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                      {pageContent.emailLabel}
                    </label>
                    <input
                      required
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      type="email"
                      className="w-full bg-[#FAFAFA] border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors font-sans"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                      {pageContent.phoneLabel}
                    </label>
                    <input
                      required
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      type="tel"
                      className="w-full bg-[#FAFAFA] border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors font-sans"
                      placeholder="+62 819 1640 1702"
                    />
                  </div>
                </div>

                <div className="space-y-2 relative">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                    {pageContent.topicLabel}
                  </label>
                  <div className="relative">
                    <select
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      className="w-full bg-[#FAFAFA] border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors text-gray-600 font-sans appearance-none pr-10"
                    >
                      {t.contact.subjects.map((subject, idx) => (
                        <option key={idx}>{subject}</option>
                      ))}
                    </select>
                    <HiChevronDown
                      size={16}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-bold uppercase tracking-wider text-gray-500 font-sans">
                    {pageContent.messageLabel}
                  </label>
                  <textarea
                    required
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={5}
                    className="w-full bg-[#FAFAFA] border border-gray-200 p-4 text-sm focus:outline-none focus:border-brand-gold transition-colors resize-none font-sans"
                    placeholder={pageContent.messagePlaceholder}
                  ></textarea>
                </div>

                <Button
                  disabled={isLoading}
                  className="w-full bg-brand-charcoal hover:bg-brand-gold text-white py-4 md:py-6 rounded-none uppercase text-[10px] md:text-xs font-bold tracking-[0.15em] md:tracking-[0.2em] transition-all duration-500 mt-4 disabled:opacity-70 disabled:cursor-not-allowed font-sans group"
                >
                  {isLoading ? (
                    <span className="flex items-center gap-2">
                      {pageContent.processing} <CgSpinner size={16} className="animate-spin" />
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <span className="hidden sm:inline">{pageContent.sendConcierge}</span>
                      <span className="sm:hidden">{pageContent.sendShort}</span>
                      <IoPaperPlane size={16} className="group-hover:translate-x-1 transition-transform" />
                    </span>
                  )}
                </Button>

                <p className="text-xs text-gray-400 text-center font-sans">
                  {pageContent.disclaimer}
                </p>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ TEASER */}
      <section className="py-20 bg-[#FAFAFA] text-center border-t border-gray-100">
        <div className="container mx-auto px-6">
          <IoChatboxEllipses className="w-10 h-10 text-brand-gold mx-auto mb-4 opacity-80" />
          <h3 className="font-serif text-2xl text-brand-charcoal mb-3">
            {pageContent.quickQuestions}
          </h3>
          <p className="text-gray-500 font-light mb-8 max-w-md mx-auto font-sans">
            {pageContent.quickDesc} <span className="font-medium">{pageContent.luxuryLiving}</span> at KinKen.
          </p>
          <a
            href="/faq"
            className="text-brand-gold text-xs font-bold uppercase tracking-widest border-b border-brand-gold pb-1 hover:text-brand-charcoal hover:border-brand-charcoal transition-all font-sans inline-flex items-center gap-2"
          >
            {pageContent.viewFaq}
            <HiArrowRight size={14} />
          </a>
        </div>
      </section>

      <Footer />
    </main>
  );
}
